import { EventEmitter } from 'events';
import path from 'path';
import { ConfigManager } from './config-manager';
import { ServiceRegistry, IService } from './service-registry';
import { ContextEngine } from './context';
import { KnowledgeBase } from './knowledge-base';
import { WorkflowManager } from './workflow-manager';
import { EnhancedTaskAnalyzer } from './enhanced-task-analyzer';
import { DynamicToolCaller, ToolDefinition } from './context/tool-caller';
import { logger } from '../utils/logger';

// 工作流集成配置
export interface WorkflowIntegrationConfig {
  projectRoot?: string;
  enableAI?: boolean;
  enableAutoStart?: boolean;
  hooks?: {
    enabled?: boolean;
    configPath?: string;
  };
  context?: {
    maxSize?: number;
    enableCompression?: boolean;
    enableOptimization?: boolean;
  };
}

// 工作流集成系统
export class WorkflowIntegration extends EventEmitter {
  // 核心组件
  public configManager: ConfigManager;
  public serviceRegistry: ServiceRegistry;
  public contextEngine: ContextEngine;
  public knowledgeBase: KnowledgeBase;
  public workflowManager: WorkflowManager;
  public taskAnalyzer: EnhancedTaskAnalyzer;
  
  private initialized = false;
  
  constructor(private config: WorkflowIntegrationConfig = {}) {
    super();
    
    // 初始化配置管理器
    this.configManager = new ConfigManager({
      projectRoot: config.projectRoot || process.cwd(),
      schemas: this.getDefaultSchemas()
    });
    
    // 初始化服务注册表
    this.serviceRegistry = new ServiceRegistry({
      enableAutoStart: config.enableAutoStart
    });
    
    // 初始化知识库
    this.knowledgeBase = new KnowledgeBase(this.configManager);
    
    // 初始化工作流管理器
    this.workflowManager = new WorkflowManager({
      projectRoot: config.projectRoot,
      enableAI: config.enableAI
    });
    
    // 延迟初始化其他组件（需要等待服务启动）
    this.taskAnalyzer = null as any;
    this.contextEngine = null as any;
  }
  
  /**
   * 初始化集成系统
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.warn('Workflow integration already initialized');
      return;
    }
    
    logger.info('Initializing workflow integration system...');
    
    try {
      // 1. 加载配置
      await this.configManager.load();
      
      // 2. 注册核心服务
      await this.registerCoreServices();
      
      // 3. 初始化知识库
      await this.knowledgeBase.initialize();
      
      // 4. 初始化工作流管理器
      if (!await this.workflowManager.isInitialized()) {
        await this.workflowManager.initialize();
      }
      
      // 5. 创建上下文引擎
      this.contextEngine = ContextEngine.createDefault(
        this.knowledgeBase,
        this.workflowManager,
        null as any // 将在下面创建 taskAnalyzer 后更新
      );
      
      // 6. 创建增强任务分析器
      this.taskAnalyzer = new EnhancedTaskAnalyzer(
        this.contextEngine,
        this.knowledgeBase,
        {
          enableAI: this.config.enableAI
        }
      );
      
      // 7. 注册默认工具
      await this.registerDefaultTools();
      
      // 8. 设置事件监听
      this.setupEventListeners();
      
      // 9. 启动所有服务
      await this.serviceRegistry.startAll();
      
      this.initialized = true;
      logger.success('Workflow integration initialized successfully!');
      this.emit('initialized');
      
    } catch (error) {
      logger.error('Failed to initialize workflow integration:', error);
      throw error;
    }
  }
  
  /**
   * 注册核心服务
   */
  private async registerCoreServices(): Promise<void> {
    // 配置管理服务
    await this.serviceRegistry.register({
      name: 'config-manager',
      version: '1.0.0',
      description: 'Configuration management service',
      
      async start() {
        logger.debug('Config manager service started');
      },
      
      async stop() {
        logger.debug('Config manager service stopped');
      },
      
      async healthCheck() {
        return {
          status: 'healthy' as const,
          timestamp: Date.now()
        };
      }
    } as IService);
    
    // 知识库服务
    await this.serviceRegistry.register({
      name: 'knowledge-base',
      version: '1.0.0',
      description: 'Knowledge base service',
      dependencies: ['config-manager'],
      
      start: async () => {
        logger.debug('Knowledge base service started');
      },
      
      stop: async () => {
        await this.knowledgeBase.save();
        logger.debug('Knowledge base service stopped');
      },
      
      healthCheck: async () => {
        const stats = this.knowledgeBase.getStats();
        return {
          status: stats.totalItems > 0 ? 'healthy' as const : 'degraded' as const,
          message: `${stats.totalItems} knowledge items`,
          timestamp: Date.now()
        };
      }
    } as IService);
    
    // 工作流管理服务
    await this.serviceRegistry.register({
      name: 'workflow-manager',
      version: '1.0.0',
      description: 'Workflow management service',
      dependencies: ['config-manager', 'knowledge-base'],
      
      start: async () => {
        logger.debug('Workflow manager service started');
      },
      
      stop: async () => {
        await this.workflowManager.stopWatching();
        logger.debug('Workflow manager service stopped');
      },
      
      healthCheck: async () => {
        const status = await this.workflowManager.getWorkflowStatus();
        return {
          status: 'healthy' as const,
          message: status ? `Workflow: ${status.type}` : 'No active workflow',
          timestamp: Date.now()
        };
      }
    } as IService);
  }
  
  /**
   * 注册默认工具
   */
  private async registerDefaultTools(): Promise<void> {
    const tools: ToolDefinition[] = [
      {
        name: 'search-knowledge',
        description: 'Search the knowledge base for relevant information',
        parameters: {
          type: 'object',
          properties: {
            query: { type: 'string', description: 'Search query' },
            limit: { type: 'number', description: 'Maximum results', default: 5 }
          },
          required: ['query']
        },
        handler: async (params) => {
          return this.knowledgeBase.search(params.query, { limit: params.limit });
        },
        category: 'knowledge',
        cost: 5,
        reliability: 0.95
      },
      
      {
        name: 'analyze-task',
        description: 'Analyze a task to understand its complexity and requirements',
        parameters: {
          type: 'object',
          properties: {
            task: { type: 'string', description: 'Task description' }
          },
          required: ['task']
        },
        handler: async (params) => {
          return this.taskAnalyzer.analyze(params.task);
        },
        category: 'analysis',
        cost: 10,
        reliability: 0.9
      },
      
      {
        name: 'recommend-workflow',
        description: 'Recommend a workflow for a given task',
        parameters: {
          type: 'object',
          properties: {
            task: { type: 'string', description: 'Task description' },
            context: { type: 'object', description: 'Additional context' }
          },
          required: ['task']
        },
        handler: async (params) => {
          const analysis = await this.taskAnalyzer.analyze(params.task);
          return analysis.workflowRecommendation;
        },
        category: 'workflow',
        cost: 15,
        reliability: 0.85
      },
      
      {
        name: 'get-project-context',
        description: 'Get current project context and status',
        parameters: {
          type: 'object',
          properties: {}
        },
        handler: async () => {
          const config = await this.workflowManager.loadConfig();
          const status = await this.workflowManager.getWorkflowStatus();
          const taskState = await this.workflowManager.getTaskState();
          
          return {
            project: config.project,
            workflowStatus: status,
            currentTask: taskState?.current,
            stats: this.knowledgeBase.getStats()
          };
        },
        category: 'context',
        cost: 5,
        reliability: 1.0
      }
    ];
    
    // 注册到工具调用器
    this.contextEngine.toolCaller.registerTools(tools);
  }
  
  /**
   * 设置事件监听
   */
  private setupEventListeners(): void {
    // 配置变更监听
    this.configManager.on('change', (event) => {
      logger.debug('Configuration changed:', event);
      this.emit('configChanged', event);
    });
    
    // 知识库事件
    this.knowledgeBase.on('itemAdded', (item) => {
      logger.debug('Knowledge item added:', item.id);
      this.emit('knowledgeAdded', item);
    });
    
    // 任务分析事件
    this.taskAnalyzer.on('analyzed', (result) => {
      logger.debug('Task analyzed:', result.complexity?.score);
      this.emit('taskAnalyzed', result);
    });
    
    // 服务事件
    this.serviceRegistry.on('serviceFailed', (name) => {
      logger.error(`Service failed: ${name}`);
      this.emit('serviceFailed', name);
    });
  }
  
  /**
   * 执行智能任务分析
   */
  async analyzeTask(taskDescription: string): Promise<any> {
    this.ensureInitialized();
    
    // 分析任务
    const analysis = await this.taskAnalyzer.analyze(taskDescription);
    
    // 基于上下文调用工具
    if (analysis.context) {
      const toolResults = await this.contextEngine.callTools(
        analysis.context,
        { description: taskDescription },
        'smart'
      );
      
      // 将工具结果添加到分析中
      (analysis as any).toolResults = toolResults;
    }
    
    return analysis;
  }
  
  /**
   * 启动任务
   */
  async startTask(taskInfo: {
    task: string;
    priority?: 'high' | 'medium' | 'low';
    autoAnalyze?: boolean;
  }): Promise<any> {
    this.ensureInitialized();
    
    // 自动分析任务
    let analysis;
    if (taskInfo.autoAnalyze !== false) {
      analysis = await this.analyzeTask(taskInfo.task);
    }
    
    // 使用工作流管理器启动任务
    const result = await this.workflowManager.startTask({
      task: taskInfo.task,
      priority: taskInfo.priority || analysis?.recommendations?.[0]?.priority || 'medium',
      context: {
        analysis: analysis ? {
          type: analysis.suggestedPrompt ? 'ai-analyzed' : 'rule-based',
          complexity: analysis.complexity,
          estimatedTime: analysis.complexity?.estimatedTime
        } : undefined
      }
    });
    
    // 如果有工作流推荐，自动启动
    if (result.success && analysis?.workflowRecommendation) {
      await this.workflowManager.startWorkflow(
        analysis.workflowRecommendation.type,
        {
          name: taskInfo.task,
          description: analysis.workflowRecommendation.reason
        }
      );
    }
    
    return { result, analysis };
  }
  
  /**
   * 获取下一个任务推荐
   */
  async getNextTask(): Promise<any> {
    this.ensureInitialized();
    
    const recommendations = await this.workflowManager.recommendNextTask();
    
    if (!recommendations || recommendations.length === 0) {
      return null;
    }
    
    // 分析第一个推荐任务
    const firstTask = recommendations[0];
    const analysis = await this.analyzeTask(firstTask.task);
    
    return {
      recommendation: firstTask,
      analysis,
      alternatives: recommendations.slice(1)
    };
  }
  
  /**
   * 添加知识
   */
  async addKnowledge(entry: {
    title: string;
    content: string;
    category: string;
    tags?: string[];
    source?: string;
  }): Promise<any> {
    this.ensureInitialized();
    
    return this.knowledgeBase.add({
      ...entry,
      tags: entry.tags || [],
      author: 'workflow-integration'
    });
  }
  
  /**
   * 搜索知识
   */
  async searchKnowledge(query: string, options?: any): Promise<any> {
    this.ensureInitialized();
    
    return this.knowledgeBase.search(query, options);
  }
  
  /**
   * 获取系统状态
   */
  async getSystemStatus(): Promise<any> {
    this.ensureInitialized();
    
    const [services, config, workflowStatus, taskState, knowledgeStats] = await Promise.all([
      this.serviceRegistry.checkAllHealth(),
      this.workflowManager.loadConfig(),
      this.workflowManager.getWorkflowStatus(),
      this.workflowManager.getTaskState(),
      this.knowledgeBase.getStats()
    ]);
    
    return {
      initialized: this.initialized,
      services: Object.fromEntries(services),
      project: config.project,
      workflow: workflowStatus,
      currentTask: taskState?.current,
      knowledge: knowledgeStats,
      uptime: process.uptime()
    };
  }
  
  /**
   * 清理资源
   */
  async dispose(): Promise<void> {
    logger.info('Disposing workflow integration...');
    
    try {
      // 停止所有服务
      await this.serviceRegistry.stopAll();
      
      // 保存知识库
      await this.knowledgeBase.save();
      
      // 停止任务监视
      await this.workflowManager.stopWatching();
      
      // 清理配置管理器
      await this.configManager.dispose();
      
      // 清理服务注册表
      await this.serviceRegistry.dispose();
      
      // 移除所有监听器
      this.removeAllListeners();
      
      this.initialized = false;
      logger.info('Workflow integration disposed');
      
    } catch (error) {
      logger.error('Error during disposal:', error);
      throw error;
    }
  }
  
  /**
   * 确保已初始化
   */
  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error('Workflow integration not initialized. Call initialize() first.');
    }
  }
  
  /**
   * 获取默认 Schemas
   */
  private getDefaultSchemas(): any {
    return {
      'workflow.project': {
        name: 'string',
        type: 'string',
        framework: 'array',
        language: 'string'
      },
      'workflow.settings': {
        autoSync: 'boolean',
        syncInterval: 'number',
        enableAI: 'boolean',
        checkMode: 'string'
      },
      'workflow.hooks': {
        enabled: 'boolean',
        configPath: 'string'
      },
      'workflow.context': {
        maxSize: 'number',
        enableCompression: 'boolean',
        enableOptimization: 'boolean'
      }
    };
  }
  
  /**
   * 创建默认实例
   */
  static async createDefault(config?: WorkflowIntegrationConfig): Promise<WorkflowIntegration> {
    const integration = new WorkflowIntegration({
      enableAI: false,
      enableAutoStart: true,
      hooks: {
        enabled: true
      },
      context: {
        maxSize: 100 * 1024,
        enableCompression: true,
        enableOptimization: true
      },
      ...config
    });
    
    await integration.initialize();
    return integration;
  }
}